'use client';

import { PropsWithChildren, useCallback } from 'react';
import { Section } from '@frontastic/extension-types';
import { classnames } from 'helpers/utils/classnames';
import Cell from './components/cell';
import Grid from './components/grid';
import TasticWrapper from './components/tastic-wrapper';
import { RendererProps } from './types';
import { highlight } from './utils/highlight';

const HeaderWrapper = ({ children }: PropsWithChildren) => (
  <header>{children}</header>
);
const MainWrapper = ({ children }: PropsWithChildren) => (
  <main>{children}</main>
);
const FooterWrapper = ({ children }: PropsWithChildren) => (
  <footer>{children}</footer>
);

const Renderer = ({
  data: pageData,
  params,
  searchParams,
  currentHighlight,
  gridClassName = '',
  wrapperClassName = '',
}: RendererProps) => {
  const { page, data } = pageData;

  const sections = [
    {
      key: 1,
      Wrapper: HeaderWrapper,
      section: page.sections.head,
    },
    {
      key: 2,
      Wrapper: MainWrapper,
      section: page.sections.main,
    },
    {
      key: 3,
      Wrapper: FooterWrapper,
      section: page.sections.footer,
    },
  ];

  const sectionsClassNames = {
    head: '',
    main: 'grow body-section',
    footer: '',
  } as Record<string, string>;

  const renderGrid = useCallback(
    (section: Section) => (
      <Grid
        key={section.sectionId}
        gridClassName={gridClassName}
        wrapperClassName={classnames(
          'relative w-full',
          wrapperClassName,
          highlight(currentHighlight === section.sectionId),
          sectionsClassNames[section.sectionId],
        )}
      >
        {section.layoutElements?.map((layoutElement) => (
          <Cell
            key={layoutElement.layoutElementId}
            configuration={layoutElement.configuration}
            isHighlighted={currentHighlight === layoutElement.layoutElementId}
          >
            {layoutElement.tastics.map((tastic) => (
              <TasticWrapper
                key={tastic.tasticId}
                data={tastic}
                params={params}
                searchParams={searchParams}
                dataSources={data.dataSources}
                isHighlighted={currentHighlight === tastic.tasticId}
              />
            ))}
          </Cell>
        ))}
      </Grid>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <div className="flex min-h-screen flex-col items-stretch justify-start">
      {sections
        .filter(({ section }) => !!section?.layoutElements?.length)
        .map(({ Wrapper, section, key }) => (
          <Wrapper key={key}>{renderGrid(section!)}</Wrapper>
        ))}
    </div>
  );
};

export default Renderer;
